import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Trash2, Save, Pencil, Mail, Copy, Download } from "lucide-react";
import { isPromoExpired } from "@/lib/promoExpiry";
import { ADMIN } from "@/constants/testIds";
import { resolveMediaUrl } from "@/lib/ddfApi";

function PromoRow({ p, onEdit, onDelete }) {
  let metaLabel = "";
  if (p.category) metaLabel = ` · ${p.category}`;
  else if (p.imageOnly) metaLabel = " · image only";
  return (
    <div className="flex items-center gap-2 border rounded p-2 text-sm">
      {p.image && (
        <img src={resolveMediaUrl(p.image)} alt="" className="h-9 w-9 rounded object-cover" />
      )}
      <span className="flex-1">
        <b>{p.name || "Image-only card"}</b>
        {metaLabel}
        {p.priceAED ? ` · AED ${p.priceAED}` : ""}
      </span>
      <Button variant="outline" size="sm" onClick={onEdit} data-testid={ADMIN.editPromoBtn}>
        <Pencil size={14} />
      </Button>
      <Button variant="destructive" size="sm" onClick={onDelete} data-testid={ADMIN.deletePromoBtn}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

function PublishedList({ items, loc, emptyLabel, onEditPromo, onRemovePromo, testid }) {
  return (
    <TabsContent value={loc} className="space-y-2 pt-3" data-testid={testid}>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{emptyLabel}</p>
      ) : (
        items.map((p) => (
          <PromoRow
            key={p.id}
            p={p}
            onEdit={() => onEditPromo(loc, p)}
            onDelete={() => onRemovePromo(loc, p.id)}
          />
        ))
      )}
    </TabsContent>
  );
}

function HoldingRow({ p, saving, checked, onToggleSelect, onEditPromo, onRemovePromo, onChangePublishAt, onScheduleItem, onPublishNow }) {
  return (
    <div className="border rounded p-2 space-y-2 text-sm" data-testid="admin-holding-row">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggleSelect(p.id)}
          className="shrink-0"
          data-testid={`admin-holding-select-${p.id}`}
        />
        {p.image && (
          <img src={resolveMediaUrl(p.image)} alt="" className="h-9 w-9 rounded object-cover" />
        )}
        <span className="flex-1">
          <b>{p.name || p.imageName || "Image-only card"}</b>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">{p.loc}</span>
          {isPromoExpired(p) && (
            <span className="ml-2 text-xs font-bold text-red-600" data-testid={`admin-holding-expired-${p.id}`}>
              EXPIRED
            </span>
          )}
          <span className="block text-xs text-muted-foreground">
            {p.category || (p.imageOnly ? "image only" : "")}
          </span>
        </span>
        <Button variant="outline" size="sm" onClick={() => onEditPromo(p.loc, p)} data-testid={ADMIN.editPromoBtn}>
          <Pencil size={14} />
        </Button>
        <Button variant="destructive" size="sm" onClick={() => onRemovePromo(p.loc, p.id)} data-testid={ADMIN.deletePromoBtn}>
          <Trash2 size={14} />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="datetime-local"
          value={(p.publishAt || "").slice(0, 16)}
          onChange={(e) => onChangePublishAt(p.loc, p.id, e.target.value)}
          className="h-9 text-xs w-auto flex-1 min-w-[170px]"
          data-testid="admin-holding-item-datetime"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => onScheduleItem(p.loc, p.id, (p.publishAt || "").slice(0, 16))}
          disabled={saving}
          data-testid="admin-holding-item-schedule"
        >
          Schedule
        </Button>
        <Button size="sm" onClick={() => onPublishNow(p.loc, p.id)} disabled={saving} data-testid="admin-holding-item-publish">
          Publish Now
        </Button>
      </div>
      {p.publishAt && (
        <p className="text-[11px] text-muted-foreground">
          Scheduled for: {p.publishAt.replace("T", " ")}
        </p>
      )}
    </div>
  );
}

export default function PromoList(props) {
  const {
    promoListTab, setPromoListTab,
    publishedDepartures, publishedArrivals, holdingItems,
    onEditPromo, onRemovePromo,
    batchPublishAt, setBatchPublishAt,
    onScheduleAll, onPublishAllNow,
    holdingEmail, setHoldingEmail, emailingHolding, onEmailHolding,
    downloadingHolding, onDownloadHolding, holdingEmailError,
    selectedHolding, toggleHoldingSelect, onDuplicateHolding,
    onChangePublishAt, onScheduleItem, onPublishNow,
    saving, persist, draft,
  } = props;

  return (
    <>
      <div className="space-y-2" data-testid={ADMIN.promoItemsList}>
        <Tabs value={promoListTab} onValueChange={setPromoListTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="departures" data-testid="admin-promo-list-tab-departures">
              Departures ({publishedDepartures.length})
            </TabsTrigger>
            <TabsTrigger value="arrivals" data-testid="admin-promo-list-tab-arrivals">
              Arrivals ({publishedArrivals.length})
            </TabsTrigger>
            <TabsTrigger value="holding" data-testid="admin-promo-list-tab-holding">
              Holding ({holdingItems.length})
            </TabsTrigger>
          </TabsList>

          <PublishedList
            items={publishedDepartures}
            loc="departures"
            emptyLabel="No published departures promos yet."
            onEditPromo={onEditPromo}
            onRemovePromo={onRemovePromo}
            testid="admin-promo-list-departures"
          />
          <PublishedList
            items={publishedArrivals}
            loc="arrivals"
            emptyLabel="No published arrivals promos yet."
            onEditPromo={onEditPromo}
            onRemovePromo={onRemovePromo}
            testid="admin-promo-list-arrivals"
          />

          <TabsContent value="holding" className="space-y-3 pt-3" data-testid="admin-promo-list-holding">
            <p className="text-xs text-muted-foreground">
              New promos wait here until you publish or schedule them. Items
              auto-go-live once their scheduled date & time passes.
            </p>
            {holdingItems.length > 0 && (
              <div className="rounded-md border p-2 space-y-2 bg-muted/40" data-testid="admin-holding-batch-bar">
                <Label className="text-xs font-semibold">Batch actions (all holding)</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="datetime-local"
                    value={batchPublishAt}
                    onChange={(e) => setBatchPublishAt(e.target.value)}
                    className="h-9 text-xs w-auto flex-1 min-w-[180px]"
                    data-testid="admin-holding-batch-datetime"
                  />
                  <Button size="sm" variant="outline" onClick={onScheduleAll} disabled={saving} data-testid="admin-holding-schedule-all">
                    Schedule All
                  </Button>
                  <Button size="sm" onClick={onPublishAllNow} disabled={saving} data-testid="admin-holding-publish-all">
                    Publish All Now
                  </Button>
                </div>
              </div>
            )}
            {holdingItems.length > 0 && (
              <div className="rounded-md border p-2 space-y-2 bg-muted/40" data-testid="admin-holding-duplicate-bar">
                <Label className="text-xs font-semibold">
                  Duplicate ticked promos ({selectedHolding.length} selected)
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving || selectedHolding.length === 0}
                        data-testid="admin-holding-duplicate-btn"
                      >
                        <Copy size={14} /> Duplicate to…
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem
                        onClick={() => onDuplicateHolding("departures")}
                        data-testid="admin-holding-duplicate-departures"
                      >
                        Departures
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onDuplicateHolding("arrivals")}
                        data-testid="admin-holding-duplicate-arrivals"
                      >
                        Arrivals
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <span className="text-[11px] text-muted-foreground">
                    Copies land back in Holding under the chosen location.
                  </span>
                </div>
              </div>
            )}
            {holdingItems.length > 0 && (
              <div className="rounded-md border p-2 space-y-2 bg-muted/40" data-testid="admin-holding-email-bar">
                <Label className="text-xs font-semibold">Email these promo cards (A4 PDF, 6 per page)</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="email"
                    placeholder="recipient@example.com"
                    value={holdingEmail}
                    onChange={(e) => setHoldingEmail(e.target.value)}
                    className="h-9 text-xs w-auto flex-1 min-w-[200px]"
                    data-testid="admin-holding-email-input"
                  />
                  <Button
                    size="sm"
                    onClick={onEmailHolding}
                    disabled={emailingHolding}
                    data-testid="admin-holding-email-send"
                  >
                    <Mail size={14} /> {emailingHolding ? "Sending…" : "Send PDF"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onDownloadHolding}
                    disabled={downloadingHolding}
                    data-testid="admin-holding-download-pdf"
                  >
                    <Download size={14} /> {downloadingHolding ? "Preparing…" : "Download PDF"}
                  </Button>
                </div>
                {holdingEmailError && (
                  <div
                    className="flex flex-wrap items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700"
                    data-testid="admin-holding-email-error"
                  >
                    <span className="flex-1">{holdingEmailError}</span>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={onDownloadHolding}
                      disabled={downloadingHolding}
                      data-testid="admin-holding-email-error-download"
                    >
                      <Download size={14} /> Download PDF instead
                    </Button>
                  </div>
                )}
              </div>
            )}
            {holdingItems.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Holding panel is empty.</p>
            ) : (
              holdingItems.map((p) => (
                <HoldingRow
                  key={p.id}
                  p={p}
                  saving={saving}
                  checked={selectedHolding.includes(p.id)}
                  onToggleSelect={toggleHoldingSelect}
                  onEditPromo={onEditPromo}
                  onRemovePromo={onRemovePromo}
                  onChangePublishAt={onChangePublishAt}
                  onScheduleItem={onScheduleItem}
                  onPublishNow={onPublishNow}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Button onClick={() => persist(draft)} disabled={saving} data-testid={ADMIN.savePromoData}>
        <Save size={14} /> Save All Promos
      </Button>
    </>
  );
}
